/**
 * Shared message-key types + normalization for the WhatsApp engine
 * surface. Used by both the public API (backend) and engine handlers
 * (baileys pod, chrome extension) so all formats round-trip cleanly.
 *
 * Three formats are in play across the codebase:
 *
 *   1. **Wire string** — `<fromMe>_<remoteJid>_<id>` (4-part with
 *      `_<participant>` for group messages). Emitted by the engines'
 *      `serializeWaMessageKey` and embedded in protocol messages.
 *      Example: `true_972544325389@s.whatsapp.net_3EB0E2AB42691D24C7A159`.
 *
 *   2. **Object form** — `{ id, remoteJid, fromMe, participant? }`.
 *      The canonical baileys / WAWeb internal shape.
 *
 *   3. **Bare message id** — just the engine-side message id with no
 *      chat or sender context, e.g. `3EB0E2AB42691D24C7A159`. This is
 *      what the public `/v1/chats/{id}/messages[].key` surfaces today,
 *      and what callers pipe back as the URL path param. To reconstruct
 *      a wire/object key from a bare id you need the `chat_id` (and,
 *      ideally, `fromMe` — though the message store can lookup with
 *      either value when the caller can't tell).
 *
 * Plus a legacy 4th shape the chrome extension still emits in some
 * places: `{ _serialized: '<wire-string>', fromMe, remote, id }`. We
 * accept it as input by reading `_serialized` first.
 */

export interface WaMessageKey {
    id: string;
    remoteJid: string;
    fromMe: boolean;
    /** Sender JID for group messages (when `fromMe=false`). */
    participant?: string;
}

export interface WaMessageKeyLike {
    id?: string | null;
    remoteJid?: string | null;
    fromMe?: boolean | null;
    participant?: string | null;
    /** WWeb-style pre-serialized wire string. Read first if present. */
    _serialized?: string | null;
    /** WWeb-style alias for remoteJid. */
    remote?: string | null;
    /** Wire-format string OR bare id — the engineRpc batch-acks adapter
     *  emits this shape. Read after `_serialized` and falls back to a
     *  bare-id interpretation (with chatId hint) when not wire-format. */
    waMessageKey?: string | null;
    /** Alias for `id` used by some MCP / batch-acks shapes. */
    messageId?: string | null;
}

/**
 * Wire-format serializer — `<fromMe>_<remoteJid>_<id>[_<participant>]`,
 * the one key serializer in the platform. The participant segment is
 * appended when non-empty (an empty-string participant is dropped).
 * Falsy `fromMe` serializes to `false_…`; nullish components serialize
 * to empty segments (the matching parser tolerates that for round-trip).
 */
export function serializeWaMessageKey(key: WaMessageKeyLike): string {
    const remote = key.remoteJid ?? key.remote ?? '';
    const base = `${key.fromMe ? 'true' : 'false'}_${remote}_${key.id ?? ''}`;
    return key.participant ? `${base}_${key.participant}` : base;
}

/**
 * Parse a wire-format string into the canonical object. Returns `null`
 * on any shape that doesn't start with `true_`/`false_` (including a
 * bare message id) — callers handle that case by falling back to
 * `normalizeWaMessageKey` with the chat-id hint, or to a store lookup.
 */
export function parseWaMessageKey(serialized: string): WaMessageKey | null {
    if (!serialized) return null;
    const prefix = serialized.startsWith('true_') ? 'true' : serialized.startsWith('false_') ? 'false' : null;
    if (!prefix) return null;
    const fromMe = prefix === 'true';
    const rest = serialized.slice(prefix.length + 1);

    // chat jid ends at the first '_' AFTER its '@' suffix.
    const firstAt = rest.indexOf('@');
    if (firstAt < 1) return null;
    const chatJidEnd = rest.indexOf('_', firstAt);
    if (chatJidEnd < 0) return null;
    const remoteJid = rest.slice(0, chatJidEnd);
    const after = rest.slice(chatJidEnd + 1);

    // 4-part fromMe-group case: after = "<msgId>_<participantJid>"
    // where participant contains '@'.
    const secondAt = after.indexOf('@');
    let id: string;
    let participant: string | undefined;
    if (secondAt > 0) {
        const partUnderscore = after.lastIndexOf('_', secondAt);
        if (partUnderscore > 0) {
            id = after.slice(0, partUnderscore);
            participant = after.slice(partUnderscore + 1);
        } else {
            id = after;
        }
    } else {
        id = after;
    }
    if (!remoteJid || !id) return null;
    const out: WaMessageKey = { id, remoteJid, fromMe };
    if (participant) out.participant = participant;
    return out;
}

/**
 * Normalize any of the four supported key shapes (or `null` /
 * `undefined`) into the canonical baileys object form. The optional
 * `hint` supplies `chatId` and/or `fromMe` when the input is just a
 * bare message id — necessary because a bare id alone can't address a
 * specific stored message.
 *
 * Returns `null` when the input can't be resolved (e.g. bare id with
 * no chat-id hint). Callers that don't know `fromMe` should pass
 * `hint.chatId` only and have the downstream lookup try both values.
 */
export function normalizeWaMessageKey(
    input: string | WaMessageKeyLike | null | undefined,
    hint?: { chatId?: string; fromMe?: boolean },
): WaMessageKey | null {
    if (input === null || input === undefined) return null;

    // String input — either a wire-format key, or a bare id requiring
    // the chat-id hint to be addressable.
    if (typeof input === 'string') {
        const parsed = parseWaMessageKey(input);
        if (parsed) return parsed;
        // Bare id (no `true_`/`false_` prefix). Need chat-id from hint.
        if (!hint?.chatId || !input) return null;
        return {
            id: input,
            remoteJid: hint.chatId,
            fromMe: hint.fromMe ?? false,
        };
    }

    // Object input — try in order: `_serialized` (WWeb legacy),
    // `waMessageKey` (batch-acks adapter), explicit id/remoteJid/remote
    // fields. `messageId` aliases `id`.
    if (typeof input === 'object') {
        const obj = input as WaMessageKeyLike;
        if (obj._serialized) {
            const parsed = parseWaMessageKey(obj._serialized);
            if (parsed) return parsed;
        }
        if (obj.waMessageKey) {
            // Recurse: waMessageKey may itself be wire-format or bare id.
            const parsed = normalizeWaMessageKey(obj.waMessageKey, hint);
            if (parsed) {
                // Carry forward fromMe if the wrapping object specified it.
                return obj.fromMe !== undefined && obj.fromMe !== null
                    ? { ...parsed, fromMe: !!obj.fromMe }
                    : parsed;
            }
        }
        const id = obj.id ?? obj.messageId ?? null;
        const remoteJid = obj.remoteJid ?? obj.remote ?? hint?.chatId ?? null;
        if (!id || !remoteJid) return null;
        const out: WaMessageKey = {
            id,
            remoteJid,
            fromMe: obj.fromMe ?? hint?.fromMe ?? false,
        };
        if (obj.participant) out.participant = obj.participant;
        return out;
    }

    return null;
}

/**
 * The "same message" identity: the message id + fromMe. The address part of a
 * key is routing and changes with its form (`@s.whatsapp.net` / `@c.us` / `@lid`,
 * with or without the participant segment), so two keys of one message share
 * this identity. A key string or key object goes through the one parser; an
 * unparseable input (a bare message id, no chat) → null.
 */
export function waMessageIdentity(key: string | WaMessageKeyLike | null | undefined): string | null {
    const k = typeof key === 'string' ? parseWaMessageKey(key) : normalizeWaMessageKey(key);
    return k ? `${k.fromMe}_${k.id}` : null;
}
